import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import Input from "components/Input";
import SimpleSelect from "components/SimpleSelect";
import ImageUploader from "components/ImageUploader";
import Heading from "components/Heading";
import useForm from "hooks/useForm";
import { OrganisationUpdateIN } from "api-client";
import { OfficeBuildingIcon } from "@heroicons/react/solid";
import OrganisationSettingsLayout from "../components/OrganisationSettingsLayout";
import {
  Organisation,
  organisationStore,
} from "features/Organisation/OrganisationStore";
import { observer } from "mobx-react-lite";
import { sidebarMenuStore } from "features/SidebarMenu/SidebarMenuStore";

type _Organisation = Omit<Organisation, "id">;

interface OrganisationLogo {
  id: string;
  image: string;
}
interface Org extends Omit<_Organisation, "logo"> {
  logo?: File[];
  selectedLogo?: OrganisationLogo[];
}

export default observer(function OrganisationAccountSettings({
  open,
  close,
}: {
  open: boolean;
  close: () => void;
}) {
  const { t } = useTranslation();
  const { organisationTypes } = organisationStore;
  const organisation = sidebarMenuStore.currentOrganisation;

  const [data, setData] = useState<Partial<Org>>({
    name: organisation?.name,
    legal_name: organisation?.legal_name,
    address: organisation?.address,
    zip_code: organisation?.zip_code,
    city: organisation?.city,
    phone: organisation?.phone,
    selectedLogo: organisation?.logo
      ? [{ id: "1", image: organisation?.logo }]
      : [],
  });

  const form = useForm(data, setData);

  async function update() {
    if (!organisation?.id) return;
    const options: OrganisationUpdateIN = {};
    if (organisation.name != data.name) options.name = data.name;
    if (organisation.legal_name != data.legal_name)
      options.legal_name = data.legal_name;
    if (organisation.address != data.address) options.address = data.address;
    if (organisation.zip_code != data.zip_code)
      options.zip_code = String(data.zip_code);
    if (organisation.city != data.city) options.city = data.city;
    if (organisation.phone != data.phone) options.phone = data.phone;
    if (organisation.organisation_type.id != data.organisation_type_id)
      options.organisation_type_id = data.organisation_type_id;

    const selectedLogo = data.logo?.length ? data.logo?.[0] : null;
    if (selectedLogo)
      await organisationStore.uploadOrganisationLogo(
        organisation.id,
        selectedLogo
      );

    if (Object.keys(options).length || selectedLogo) {
      await organisationStore.updateOrganisation(organisation?.id, options);
    }
  }

  function header() {
    return (
      <div className="flex items-center">
        <OfficeBuildingIcon width={20} />
        <Heading size="title" className="pl-2 !mb-0">
          {`${organisation?.name} ${t(
            "organisationSettings.myOrganisation.settings"
          )}`}
        </Heading>
      </div>
    );
  }

  function logoTile() {
    return (
      <div className="border-b pb-2">
        <div className="font-semibold mb-6 text-lg">
          {t("organisationSettings.myOrganisation.logo")}
        </div>
        <ImageUploader
          name="logo"
          onChange={form.handleChange}
          images={data.selectedLogo ? data.selectedLogo : []}
          multiple={false}
        />
      </div>
    );
  }

  function organisationTile() {
    return (
      <div className="border-b pb-2 mt-4">
        <div className="font-semibold mb-6 text-lg">
          {t("organisationSettings.myOrganisation.organisation")}
        </div>
        <Input
          labelName={t("organisationSettings.myOrganisation.name")}
          placeholder={t("organisationSettings.myOrganisation.namePlaceholder")}
          noMargin
          value={data.name}
          name="name"
          className="mb-1"
          onChange={form.handleChange}
          required
          showMandatoryAlert
          infoText={t("organisationSettings.myOrganisation.nameInfos")}
        />
        {organisationTypes?.length > 0 && (
          <SimpleSelect
            labelName={t("organisationSettings.myOrganisation.companyType")}
            options={organisationTypes}
            name="organisation_type_id"
            placeholder={t(
              "organisationSettings.myOrganisation.companyTypePlaceholder"
            )}
            containerClassName="pt-3"
            onChange={form.handleChange}
            current={organisation?.organisation_type?.id}
          />
        )}
      </div>
    );
  }

  function contactTile() {
    return (
      <div className="pt-4">
        <div className="font-semibold mb-6 text-lg">
          {t("organisationSettings.myOrganisation.contact")}
        </div>
        <Input
          labelName={t("organisationSettings.myOrganisation.companyName")}
          name="legal_name"
          placeholder={t(
            "organisationSettings.myOrganisation.companyNamePlaceholder"
          )}
          noMargin
          className="mb-1"
          value={String(data.legal_name ?? "")}
          onChange={form.handleChange}
          infoText={t("organisationSettings.myOrganisation.companyNameInfos")}
        />
        <Input
          labelName={t("organisationSettings.myOrganisation.companyAddress")}
          name="address"
          placeholder={t(
            "organisationSettings.myOrganisation.companyAddressPlaceholder"
          )}
          noMargin
          value={String(data.address ?? "")}
          containerClassName="mb-1 mt-4"
          onChange={form.handleChange}
        />
        <div className="flex w-full justify-between gap-4">
          <Input
            labelName={t("organisationSettings.myOrganisation.zipCode")}
            name="zip_code"
            placeholder={t(
              "organisationSettings.myOrganisation.zipCodePlaceholder"
            )}
            noMargin
            value={String(data.zip_code === null ? "" : data.zip_code)}
            containerClassName="mb-1 mt-4 w-full"
            onChange={form.handleChange}
          />
          <Input
            labelName={t("organisationSettings.myOrganisation.city")}
            name="city"
            placeholder={t(
              "organisationSettings.myOrganisation.cityPlaceholder"
            )}
            noMargin
            value={String(data.city ?? "")}
            containerClassName="mb-1 mt-4 w-full"
            onChange={form.handleChange}
          />
        </div>
        <Input
          labelName={t("organisationSettings.myOrganisation.phone")}
          name="phone"
          placeholder={t(
            "organisationSettings.myOrganisation.phonePlaceholder"
          )}
          noMargin
          type="number"
          noArrow
          value={String(data.phone)}
          containerClassName="mt-4 pb-5 w-full"
          onChange={form.handleChange}
        />
      </div>
    );
  }

  return (
    <OrganisationSettingsLayout
      open={open}
      close={close}
      update={update}
      header={header()}
      hasError={data.name === ""}
    >
      <div className="p-5 text-sm h-[calc(100vh-200px)] overflow-y-auto scrollbar">
        {logoTile()}
        {organisationTile()}
        {contactTile()}
      </div>
    </OrganisationSettingsLayout>
  );
});
